/**
 * The console's own URL builder.
 *
 * Separate from `@/utils/routes` on purpose: that file is the old app's map and every deep link in
 * the product goes through it. The console is a subtree with one entry point, and keeping its URLs
 * here means removing the console is `git rm -r` plus one line in app.tsx.
 *
 * The prefix stays inside the app's existing grammar — `/company/:id/workspace/:id/...` — and the
 * console is expected to shed it later, once enough of the new surface exists that it owns its own
 * URL space. Everything after `/console` is already the console's, so that move is a change to one
 * prefix rather than to any route below it.
 *
 * ⚑ The reason is NOT that the console needs the ids in the URL. ConsoleRoot takes both as route
 * params and passes them explicitly to useWorkspaceCapabilities, so the regex at
 * useWorkspaceCapabilities.ts:43-44 never runs for this subtree. What does depend on the shape is
 * the ambient machinery — CompanyProvider.tsx:35, useUserProfile.tsx:43 and
 * useCompanyWorkspaceNav.ts:23 each re-derive the company from `/company/([^/]+)` and have no
 * override. Keeping the prefix keeps those working, which is also what will make a company-scoped
 * admin surface at `/company/:id/console/...` cheap: it inherits company context for free.
 */

import type { FilterAst, Lens } from './data/adapter'
import { LENS_PARAM, filterQuery } from './data/filterUrl'

/** The URL segment the console lives under. The route in app.tsx must agree with this. */
export const CONSOLE_SEGMENT = 'console'

/**
 * Is this path inside the console?
 *
 * ⚑ Used by `GlobalErrorHandler` to stay out of the console's way: the console reports its own
 * failures inline, in the pane the reader is looking at, so the app-wide 422 toast would say the
 * same thing a second time in a design system the console shares nothing with.
 */
export function isConsolePath(path: string | undefined | null): boolean {
  return !!path && new RegExp(`/${CONSOLE_SEGMENT}(/|$)`).test(path)
}

/**
 * The one grid surface (ORB-815): people and the companies they work at, read through a lens. The
 * lens rides in the query string beside the filter, so a flip is a new URL on the same surface.
 */
export const PROSPECTS = 'prospects' as const

/**
 * The front door: Webster at the bare `/console`, full screen (ORB-773).
 *
 * The value is the segment a URL would carry if one ever pointed here explicitly. Nothing routes
 * to it today — the front door IS the bare index — but naming it is what lets the sidebar mark
 * itself current and a switch be exhaustive.
 */
export const FRONT_DOOR = 'webster' as const

/**
 * Every thread you have, across every workspace you can reach (ORB-794, split out by ORB-796).
 *
 * ⚑ Its own surface, not a section of the account page. Both the rail's "All threads" and your
 * name at the foot used to open one screen that was a thread list wearing an identity row, so two
 * entries claimed to go to different places and did not. The list is a place you work; your
 * account is a place you visit — see {@link ACCOUNT}.
 *
 */
export const THREADS = 'threads' as const

/**
 * Your account: who you are and how the console looks (ORB-794, narrowed by ORB-796).
 *
 * ⚑ Thin on purpose, and expected to stay thin for a while. It holds an identity row and the theme
 * because that is genuinely all the console has to say about you today — the alternative was
 * leaving the thread list here to give the page bulk, which is what made two rail entries lead to
 * the same screen. Sign-out, notifications and keys land here when they exist.
 *
 * ⚑ It lives inside the console's `/company/:id/workspace/:id/` prefix even though what it shows
 * belongs to no workspace. The prefix is what carries company context — `CompanyProvider`,
 * `useUserProfile` and `useCompanyWorkspaceNav` all re-derive the company from `/company/([^/]+)` —
 * so a top-level `/account` would need a shell and a company of its own before it could render one
 * row. When the console sheds this prefix, both of these shed it in the same move.
 */
export const ACCOUNT = 'account' as const

/**
 * The knowledge base (ORB-830): what Webster writes a company's emails and pages from. Anyone who can see the workspace
 * reads it; who changes it depends on the level (ORB-817). It has no composer.
 */
export const KNOWLEDGE_BASE = 'knowledge-base' as const

/**
 * Campaigns (ORB-849): a place you navigate rather than a page.
 *
 * ⚑⚑ The only console surface with a path under it. Everything else is one segment, and the rail
 * marks the one you are on; here the rail shows the path — Campaigns, then the campaign, then the
 * web — with that level's sections beneath it. `parseCampaignRoute` is what turns the URL into that
 * path, and it is the whole of the difference: the surface is still resolved by `surfaceFromRest`
 * like any other.
 */
export const CAMPAIGNS = 'campaigns' as const

/** The workspace's scheduled touchpoints, launches and online events (ORB-949). */
export const CALENDAR = 'calendar' as const

/**
 * Workspace lists (ORB-806): saved People searches. `/lists` is the directory and `/lists/:id` opens
 * one on the Prospects grid, in list mode rather than by routing to Prospects.
 */
export const LISTS = 'lists' as const

/**
 * The workspace's landing page templates (ORB-1103): read, previewed, renamed and removed here. Saving one and starting
 * a page from one both happen in the app's landing page builder, which the console does not have.
 */
export const TEMPLATES = 'templates' as const

/** Where a lists URL lands. `missing` is a URL this subtree does not have, never a silent directory. */
export type ListRoute =
  | { page: 'index' }
  | { page: 'list'; listId: string }
  | { page: 'missing' }

/** The segment a web sits under, inside its campaign: `/campaigns/:campaignId/webs/:webId`. */
const WEBS = 'webs' as const

/**
 * The segment a campaign list's segments sit under, inside its Audience section:
 * `/campaigns/:campaignId/audience/lists/:campaignListId/segments/:segmentId` (ORB-1043).
 */
const SEGMENTS = 'segments' as const

/**
 * A campaign's ten sections, in rail order. Some are built here; the rest render a signpost
 * saying where they still live (ORB-849).
 *
 * ⚑ All ten exist from day one on purpose. A rail that grows an entry each time one is ported
 * changes shape under the reader eight more times, and nobody can tell from the console whether a
 * section is missing or simply somewhere else.
 */
export const CAMPAIGN_SECTIONS = [
  'overview', 'audience', 'webs', 'leads', 'calendar',
  'meetings', 'analytics', 'broadcasts', 'knowledge-base', 'settings',
] as const
export type CampaignSection = (typeof CAMPAIGN_SECTIONS)[number]

/**
 * A web's ten sections, in rail order. `details` and `meetings` are built here; the rest are signposts.
 *
 * ⚑ `quiz` belongs to a Download web only (ORB-946). The route still resolves on any web, because the
 * type is not known until the web loads; `sections.tsx` says which types have it, and the signpost
 * refuses it for the others.
 */
export const WEB_SECTIONS = [
  'details', 'quiz', 'activations', 'broadcasts', 'meetings', 'calendar',
  'leads', 'assets', 'landing-page', 'settings',
] as const
export type WebSection = (typeof WEB_SECTIONS)[number]

/**
 * A campaign list opened from its campaign's Audience section, and one of its segments when `segmentId` is set
 * (ORB-1008, ORB-1043).
 *
 * ⚑ A field on the campaign page rather than a page of its own. The route stays on `section: 'audience'`, so the
 * rail keeps Audience current and the seat, the sidebar and the top bar read it without knowing lists exist.
 */
export interface CampaignAudienceRoute {
  campaignListId: string
  segmentId: string | null
}

/** Where a campaign URL lands. `missing` is a URL this subtree does not have, never a silent index. */
export type CampaignRoute =
  | { page: 'index' }
  | { page: 'new' }
  | { page: 'campaign'; campaignId: string; section: CampaignSection; audience?: CampaignAudienceRoute }
  | { page: 'web'; campaignId: string; webId: string; section: WebSection }
  | { page: 'missing' }

/**
 * The literal segment a campaign being created sits at, before it has an id.
 *
 * ⚑ Checked before the id, so a campaign can never be addressed as `new`. Admin spells its two
 * create pages the same way (`/workspaces/new`, `/domains/new`); this is that pattern, not a new one.
 */
const NEW = 'new' as const

/** Which entries the knowledge base lists: all of them, Webster's drafts nobody has reviewed, or the empty ones. */
export type KnowledgeBaseShow = 'all' | 'drafts' | 'empty'

/**
 * Anything the console can be standing on: the front door, a page of your own, Prospects, the
 * knowledge base, or anywhere inside Campaigns.
 *
 * ⚑ Campaigns is one member however deep the URL goes. The surface is what the rail highlights and
 * what a workspace switch carries you to — a campaign belongs to the workspace you are leaving, so
 * switching lands on the index, not on a campaign id that means nothing there.
 */
export type ConsoleSurface =
  | typeof FRONT_DOOR
  | typeof THREADS
  | typeof ACCOUNT
  | typeof PROSPECTS
  | typeof KNOWLEDGE_BASE
  | typeof CAMPAIGNS
  | typeof CALENDAR
  | typeof LISTS
  | typeof TEMPLATES

/**
 * A surface that owns a Webster thread.
 *
 * ⚑ Every console surface except the two pages of your own, and the exclusion is the point: the
 * thread list is where you go to SEE your threads and the account page says who you are, and
 * neither has a composer. Typing them out of this union is what keeps `consoleLocation` exhaustive
 * without inventing a chat location for a screen that never opens a conversation.
 */
export type ThreadSurface = typeof FRONT_DOOR | typeof PROSPECTS | typeof CAMPAIGNS

export function isProspects(surface: ConsoleSurface): surface is typeof PROSPECTS {
  return surface === PROSPECTS
}

export function consoleBase(clientCompanyId: string, workspaceId: string): string {
  return `/company/${clientCompanyId}/workspace/${workspaceId}/${CONSOLE_SEGMENT}`
}

export function consoleRoutes(clientCompanyId: string, workspaceId: string) {
  const base = consoleBase(clientCompanyId, workspaceId)
  const campaignListPath = (campaignId: string, campaignListId: string) =>
    `${base}/${CAMPAIGNS}/${encodeURIComponent(campaignId)}/audience/${LISTS}/${encodeURIComponent(campaignListId)}`
  return {
    root: () => base,
    prospects: () => `${base}/${PROSPECTS}`,
    threads: () => `${base}/${THREADS}`,
    account: () => `${base}/${ACCOUNT}`,
    /** This workspace's entries unless `companyWide`, listing what `show` names. */
    knowledgeBase: ({ show = 'all', companyWide = false }: { show?: KnowledgeBaseShow; companyWide?: boolean } = {}) =>
      `${base}/${KNOWLEDGE_BASE}${knowledgeBaseSearch(show, companyWide ? 'company' : null)}`,
    /** A campaign's Knowledge base section, at the campaign's own level unless `level` names one above it (ORB-817). */
    campaignKnowledgeBase: (
      campaignId: string,
      { show = 'all', level = null }: { show?: KnowledgeBaseShow; level?: KnowledgeBaseLevelParam | null } = {},
    ) => `${base}/${CAMPAIGNS}/${encodeURIComponent(campaignId)}/${KNOWLEDGE_BASE}${knowledgeBaseSearch(show, level)}`,
    calendar: () => `${base}/${CALENDAR}`,
    campaigns: () => `${base}/${CAMPAIGNS}`,
    newCampaign: () => `${base}/${CAMPAIGNS}/${NEW}`,
    /**
     * A campaign, on the section named — its home when none is.
     *
     * ⚑ `overview` and `details` are spelled into the URL rather than left off. The rail's current
     * entry is read back out of the URL, so a bare `/campaigns/:id` would have to mean "overview"
     * in one place and "no section" in another; parsing it to the first section keeps one answer.
     */
    campaign: (campaignId: string, section: CampaignSection = 'overview') =>
      `${base}/${CAMPAIGNS}/${encodeURIComponent(campaignId)}/${section}`,
    web: (campaignId: string, webId: string, section: WebSection = 'details') =>
      `${base}/${CAMPAIGNS}/${encodeURIComponent(campaignId)}/${WEBS}/${encodeURIComponent(webId)}/${section}`,
    /** A campaign list's page, inside its campaign's Audience section (ORB-1008). */
    campaignList: (campaignId: string, campaignListId: string) => campaignListPath(campaignId, campaignListId),
    /** One segment of a campaign list as the Audience Builder's grid, on the lens named; People is the bare URL (ORB-1043). */
    campaignSegment: (campaignId: string, campaignListId: string, segmentId: string, lens: Lens = 'people') =>
      `${campaignListPath(campaignId, campaignListId)}/${SEGMENTS}/${encodeURIComponent(segmentId)}`
      + (lens === 'companies' ? `?${LENS_PARAM}=companies` : ''),
    lists: () => `${base}/${LISTS}`,
    /** One list, on the lens named. People is the bare URL, as it is on Prospects. */
    list: (listId: string, lens: Lens = 'people') =>
      `${base}/${LISTS}/${encodeURIComponent(listId)}${lens === 'companies' ? `?${LENS_PARAM}=companies` : ''}`,
    templates: () => `${base}/${TEMPLATES}`,
  }
}

/** Resolves everything after `/console`, for the lists surface. Never throws, like `parseCampaignRoute`. */
export function listRouteFromRest(rest: string | undefined): ListRoute {
  const segments = (rest ?? '').split('/').filter(Boolean)
  if (segments[0] !== LISTS) return { page: 'missing' }
  if (segments.length === 1) return { page: 'index' }
  if (segments.length > 2) return { page: 'missing' }
  try {
    const listId = decodeURIComponent(segments[1])
    return listId === '' ? { page: 'missing' } : { page: 'list', listId }
  } catch {
    return { page: 'missing' }
  }
}

/**
 * Resolves everything after `/console/campaigns`.
 *
 * ⚑⚑ Both section lists are arrays read with `includes`, never object literals keyed by segment.
 * The segment arrives off a URL, so an object literal answers for every `Object.prototype` member —
 * `/campaigns/x/constructor` would resolve to a function and read as a real section. `admin/routes.ts`
 * and `data/threads.ts` both spell this out; it is the same rule.
 *
 * Anything it does not recognise is `missing`, not the index. A URL this subtree does not have
 * should say so rather than quietly answering a question about `/campaigns/x/leeds` with a
 * campaign's overview.
 */
export function parseCampaignRoute(rest = ''): CampaignRoute {
  const segments = rest.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean)
  // The caller strips `campaigns`; tolerate it being left on so this reads the same either way.
  if (segments[0] === CAMPAIGNS) segments.shift()
  if (segments.length === 0) return { page: 'index' }
  if (segments[0] === NEW) return segments.length === 1 ? { page: 'new' } : { page: 'missing' }

  let campaignId: string
  try {
    campaignId = decodeURIComponent(segments[0])
  } catch {
    // A malformed escape is a bad link, not a crash. `threadFromHash` says why this never throws.
    return { page: 'missing' }
  }
  if (campaignId === '') return { page: 'missing' }

  if (segments.length === 1) return { page: 'campaign', campaignId, section: 'overview' }

  // ⚑ `webs` is the one segment that means two things. Bare, it is the campaign's Webs section and
  // falls through below; with an id after it, it is the collection a web hangs off. Taking this
  // branch on the bare form resolved `/campaigns/:id/webs` — the rail's own Webs entry — as a
  // truncated web URL, so the section the rail linked to was `missing`.
  if (segments[1] === WEBS && segments.length > 2) {
    if (segments.length > 4) return { page: 'missing' }
    let webId: string
    try {
      webId = decodeURIComponent(segments[2])
    } catch {
      return { page: 'missing' }
    }
    if (webId === '') return { page: 'missing' }
    const section = segments[3] ?? 'details'
    if (!isWebSection(section)) return { page: 'missing' }
    return { page: 'web', campaignId, webId, section }
  }

  // Audience is the other section with pages under it: a campaign list, and a segment of one (ORB-1043).
  if (segments[1] === 'audience' && segments.length > 2) {
    const audience = audienceFromSegments(segments.slice(2))
    return audience === null ? { page: 'missing' } : { page: 'campaign', campaignId, section: 'audience', audience }
  }

  if (segments.length > 2) return { page: 'missing' }
  const section = segments[1]
  return isCampaignSection(section) ? { page: 'campaign', campaignId, section } : { page: 'missing' }
}

/** `lists/:campaignListId`, or `lists/:campaignListId/segments/:segmentId`, after `audience`; null for anything else. */
function audienceFromSegments(segments: readonly string[]): CampaignAudienceRoute | null {
  if (segments[0] !== LISTS || (segments.length !== 2 && segments.length !== 4)) return null
  if (segments.length === 4 && segments[2] !== SEGMENTS) return null
  try {
    const campaignListId = decodeURIComponent(segments[1])
    const segmentId = segments.length === 4 ? decodeURIComponent(segments[3]) : null
    return campaignListId === '' || segmentId === '' ? null : { campaignListId, segmentId }
  } catch {
    return null
  }
}

export function isCampaignSection(segment: string): segment is CampaignSection {
  return (CAMPAIGN_SECTIONS as readonly string[]).includes(segment)
}

export function isWebSection(segment: string): segment is WebSection {
  return (WEB_SECTIONS as readonly string[]).includes(segment)
}

const KNOWLEDGE_BASE_LEVEL_PARAM = 'level'
const KNOWLEDGE_BASE_SHOW_PARAM = 'show'

/**
 * A level a knowledge base URL can name. The page's own level — the workspace on the Knowledge base page, the campaign
 * in a campaign's section — is never spelled, so a bare URL always lands on it.
 */
export type KnowledgeBaseLevelParam = 'company' | 'workspace'

function knowledgeBaseSearch(show: KnowledgeBaseShow, level: KnowledgeBaseLevelParam | null): string {
  const query = new URLSearchParams()
  if (level !== null) query.set(KNOWLEDGE_BASE_LEVEL_PARAM, level)
  if (show !== 'all') query.set(KNOWLEDGE_BASE_SHOW_PARAM, show)
  const search = query.toString()
  return search === '' ? '' : `?${search}`
}

/** What a knowledge base URL asks for. Anything it does not recognise reads as the default rather than as an error. */
export function knowledgeBaseViewFromQuery(
  query: Record<string, string> | undefined,
): { show: KnowledgeBaseShow; level: KnowledgeBaseLevelParam | null } {
  const show = query?.[KNOWLEDGE_BASE_SHOW_PARAM]
  const level = query?.[KNOWLEDGE_BASE_LEVEL_PARAM]
  return {
    show: show === 'drafts' || show === 'empty' ? show : 'all',
    level: level === 'company' || level === 'workspace' ? level : null,
  }
}

/**
 * Prospects' URL with a lens and a filter on it (ORB-759, ORB-815).
 *
 * The query string is the filter's only home — there is no saved-view object, and there does not
 * need to be one for a narrowed list to survive a reload or a paste into Slack. `filterQuery`
 * carries the whole encoding, including its version, so nothing here knows the grammar.
 */
export function consoleViewUrl(
  clientCompanyId: string,
  workspaceId: string,
  lens: Lens,
  filter: FilterAst,
  /**
   * The thread this grid should show (ORB-796, ORB-799) — a handoff moving its conversation here,
   * or a filter restore keeping the one already open.
   */
  threadId?: string | null,
): string {
  return `${consoleBase(clientCompanyId, workspaceId)}/${PROSPECTS}${filterQuery(filter, lens, threadId)}`
}

/**
 * A link back to a Webster thread (ORB-768) — the provenance pointer a handoff carries.
 *
 * Lands on the console index, which since ORB-773 IS the front door, carrying the thread it should
 * open as a fragment.
 */
export function consoleThreadUrl(clientCompanyId: string, workspaceId: string, threadId: string): string {
  return `${consoleBase(clientCompanyId, workspaceId)}#thread=${encodeURIComponent(threadId)}`
}

/**
 * The query parameter that opens the front door already asking something (ORB-819), the mirror of
 * `#thread=`: that one opens a conversation, this one starts one.
 *
 * ⚑⚑ It carries an intent, never a sentence. A URL is something anyone can send an operator, and the
 * front door sends what it names the moment it lands, as them and with their tools. So only the intents
 * below are honoured, and a grid handing over a task keeps the user's words out of the URL entirely.
 */
export const ASK_PARAM = 'ask'

/** Start onboarding a customer: the Customers page's primary action. */
export const ONBOARD_CUSTOMER = 'onboard-customer'

/** A task a grid handed over. Its words wait in `webster/taskHandoff`, never in the URL. */
export const HANDED_TASK = 'handed-task'

/** What onboarding is asked with, from the Customers page and from the front door's own starter alike. */
export const ONBOARD_CUSTOMER_ASK = 'Onboard a customer'

/** Start the knowledge base agent (ORB-830): the knowledge base page's button and the front door's starter. */
export const SET_UP_KNOWLEDGE_BASE = 'set-up-knowledge-base'

/** What the knowledge base agent is asked with, wherever it starts. */
export const SET_UP_KNOWLEDGE_BASE_ASK = 'Set up the knowledge base'

/**
 * Start the knowledge base agent on the workspace the console is in, from its own website (ORB-1027): the knowledge base
 * page's button at a workspace that has one. The agent then reads that site and writes the workspace's own entries.
 */
export const SET_UP_WORKSPACE_KNOWLEDGE_BASE = 'set-up-workspace-knowledge-base'

/** What the knowledge base agent is asked with when it starts at a workspace. */
export const SET_UP_WORKSPACE_KNOWLEDGE_BASE_ASK = "Set up this workspace's knowledge base from its own website"

/** A `Map`, because the key arrives off a URL: see `data/threads.ts` on object literals. */
const ASKS = new Map<string, string>([
  [ONBOARD_CUSTOMER, ONBOARD_CUSTOMER_ASK],
  [SET_UP_KNOWLEDGE_BASE, SET_UP_KNOWLEDGE_BASE_ASK],
  [SET_UP_WORKSPACE_KNOWLEDGE_BASE, SET_UP_WORKSPACE_KNOWLEDGE_BASE_ASK],
])

export function consoleAskUrl(
  clientCompanyId: string,
  workspaceId: string,
  intent: typeof ONBOARD_CUSTOMER | typeof SET_UP_KNOWLEDGE_BASE | typeof SET_UP_WORKSPACE_KNOWLEDGE_BASE | typeof HANDED_TASK,
): string {
  return `${consoleBase(clientCompanyId, workspaceId)}?${ASK_PARAM}=${intent}`
}

/** The sentence a fixed intent asks, or null for anything else, including free text. */
export function askForIntent(intent: string | undefined): string | null {
  return intent === undefined ? null : ASKS.get(intent) ?? null
}

/** The query parameter a Meetings screen opens one recording from, spelled as `meetings/model.ts` spells it. */
const MEETING_PARAM = 'meeting'

/**
 * A campaign or web page a hand-off card goes to (ORB-860): the section named, a recording on it when a
 * meeting is named, and `ask=handed-task` when a task waits for the page in `webster/taskHandoff`.
 *
 * A section the level does not have lands on the object's home rather than on a page that is missing.
 */
export function consoleDestinationUrl(
  clientCompanyId: string,
  workspaceId: string,
  destination: { campaignId: string; webId: string | null; section: CampaignSection | WebSection; meetingId: string | null },
  handedTask: boolean,
): string {
  const pages = consoleRoutes(clientCompanyId, workspaceId)
  const { campaignId, webId, section, meetingId } = destination
  const path = webId
    ? pages.web(campaignId, webId, isWebSection(section) ? section : 'details')
    : pages.campaign(campaignId, isCampaignSection(section) ? section : 'overview')

  const query = new URLSearchParams()
  if (meetingId) query.set(MEETING_PARAM, meetingId)
  if (handedTask) query.set(ASK_PARAM, HANDED_TASK)
  const search = query.toString()
  return search === '' ? path : `${path}?${search}`
}

/**
 * The thread id a `consoleThreadUrl` carries, or null for any other fragment.
 *
 * Never throws. `decodeURIComponent` raises `URIError` on a malformed escape — `#thread=%E0%A4%A`
 * — and this is read during render, where a throw is the console's
 * ErrorBoundary and a blank page rather than a link that simply does nothing.
 */
export function threadFromHash(hash: string | undefined): string | null {
  const match = /^#?thread=(.+)$/.exec(hash ?? '')
  if (!match) return null
  try {
    return decodeURIComponent(match[1])
  } catch {
    return null
  }
}

/**
 * Resolves the surface from the router's `rest` — the remainder after the `/console/*` match, which
 * arrives as `/prospects` or `''`.
 *
 * ⚑ An empty rest is the front door, which is the change ORB-773 makes: the bare `/console` used to
 * fall through to the People grid, so the console had no index of its own and `consoleThreadUrl`
 * pointed at a place that did not exist.
 *
 * An unknown segment lands there too rather than 404ing. The subtree owns its whole URL space, so a
 * stale bookmark should land somewhere real — and of the surfaces available, the one that can hold
 * a conversation is the one that can explain where you have arrived. Falling to a grid instead
 * would silently answer a question about an unknown segment with a list of people. The retired
 * `/people` and `/companies` land there too (ORB-815): there is no alias for them.
 */
export function surfaceFromRest(rest: string | undefined): ConsoleSurface {
  const segment = (rest ?? '').split('/').filter(Boolean)[0]
  if (segment === PROSPECTS) return PROSPECTS
  if (segment === CALENDAR) return CALENDAR
  if (segment === LISTS) return LISTS
  if (segment === THREADS) return THREADS
  if (segment === ACCOUNT) return ACCOUNT
  if (segment === KNOWLEDGE_BASE) return KNOWLEDGE_BASE
  if (segment === TEMPLATES) return TEMPLATES
  // Every depth under `campaigns` is this one surface; `parseCampaignRoute` reads the path itself.
  if (segment === CAMPAIGNS) return CAMPAIGNS
  return FRONT_DOOR
}

/** What `parseCampaignRoute` should be handed: everything after `/console/campaigns`. */
export function campaignRestFromRest(rest: string | undefined): string {
  return (rest ?? '').replace(/^\/+/, '').split('/').filter(Boolean).slice(1).join('/')
}

/**
 * The campaigns path, straight from the console's `rest`.
 *
 * ⚑ Resolved once and handed to both the main area and the rail. Parsing it twice is how a rail
 * comes to highlight one section while the page shows another.
 */
export function campaignRouteFromRest(rest: string | undefined): CampaignRoute {
  return parseCampaignRoute(campaignRestFromRest(rest))
}

/**
 * What opening a thread needs: a thread row as the list sends it, or the same fields read off a session
 * for the link back to a parent (ORB-860).
 */
export interface ThreadLink {
  ClientCompanyId: string
  WorkspaceId: string
  Id: string
  /**
   * The list the thread was last held over, already encoded (ORB-796). Spliced in rather than
   * decoded and re-encoded: it is the same `v`/`f`/`lens` the URL carries, and a round trip through
   * the tree here would put this file in the filter grammar it deliberately knows nothing about.
   */
  ProspectFilter?: string | null
  /** The campaign, and the web, a Campaigns thread is about. */
  CampaignId?: string | null
  WebId?: string | null
}

/**
 * A link that opens one thread on the surface it belongs to (ORB-794), or null for a Campaigns thread
 * that names no campaign.
 *
 * ⚑ Takes the thread's OWN company and workspace, not the ones in the current URL. The account page
 * spans every workspace the user can reach, so opening a row from it is routinely a move to another
 * workspace — and building this from the page's own ids would land on the right surface of the wrong
 * workspace, where the thread does not exist.
 *
 * The front door reads the thread from the fragment; a grid surface and a campaign or web page read it
 * from `t`. Two spellings because the fragment is what `consoleThreadUrl` has always used for the front
 * door and changing it would break every handoff link already sent, while a grid needs the value to
 * survive the filter round trip that rewrites its query string. A Campaigns thread opens on its object's
 * home: a campaign's Overview, or a web's Details.
 */
export function consoleThreadOpenUrl(thread: ThreadLink, surface: ConsoleSurface): string | null {
  const threadId = encodeURIComponent(thread.Id)
  if (surface === CAMPAIGNS) {
    if (!thread.CampaignId) return null
    const pages = consoleRoutes(thread.ClientCompanyId, thread.WorkspaceId)
    const home = thread.WebId ? pages.web(thread.CampaignId, thread.WebId) : pages.campaign(thread.CampaignId)
    return `${home}?t=${threadId}`
  }
  const base = consoleBase(thread.ClientCompanyId, thread.WorkspaceId)
  if (!isProspects(surface)) return `${base}#thread=${threadId}`
  const filterPart = thread.ProspectFilter ? `${thread.ProspectFilter}&` : ''
  return `${base}/${PROSPECTS}?${filterPart}t=${threadId}`
}

/**
 * The thread id a grid surface was asked to open, or null.
 *
 * Never throws, for the reason `threadFromHash` does not: this is read during render, where a
 * `URIError` from a malformed escape is the console's ErrorBoundary and a blank page rather than a
 * link that simply does nothing.
 */
export function threadFromQuery(query: Record<string, string> | undefined): string | null {
  const raw = query?.t
  if (!raw) return null
  try {
    return decodeURIComponent(raw)
  } catch {
    return null
  }
}
